import { toCalendarDate, toTime } from "@internationalized/date";
import { InfrastructureForm } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/formSchema";
import { FormFieldWithCustomInput } from "features/CenterContent/shared/operationalInformation/components/FormFieldWithCustomInput";
import { useOperationalInformationTypes } from "features/CenterContent/shared/operationalInformation/hooks/useOperationalInformationTypes";
import {
  filterOpInfoTypesInfrastructure,
  filterOpInfoTypesTrain,
} from "features/CenterContent/shared/operationalInformation/utils";
import { EitherTrainFormSchema } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { FC } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  DropsDatePicker,
  Alert,
  Field,
  HStack,
  Skeleton,
  Stack,
  TimePicker,
} from "shared/components/Spor";
import {
  getInfrastructureCustomInputs,
  getTrainCustomInputs,
} from "shared/types/form";
import { useTheme } from "styled-components";

type PrognosisProps = {
  formPrefix: "infrastructureForm" | "trainForm" | `\${groupKey}.0.trainForm`;
};

export const Prognosis: FC<PrognosisProps> = ({ formPrefix }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { control, setValue, getValues } = useFormContext<
    InfrastructureForm | EitherTrainFormSchema
  >();
  const prognosisType = useWatch({
    control,
    name: `${formPrefix}.prognosisType.type`,
  });
  const prognosisTime = getValues(`${formPrefix}.prognosisTime`);

  const { data: opInfoTypes, status: opInfoTypesStatus } =
    useOperationalInformationTypes(t);

  const showDateTimeFields =
    prognosisType === "ESTIMATED_OPENING" ||
    prognosisType === "ESTIMATED_INFO_UPDATE";

  const getCustomInputs = () => {
    if (formPrefix === "infrastructureForm")
      return getInfrastructureCustomInputs(t);
    // Done explicitly in order to support both trainInfo and trainBatch
    return {
      prognosisType: {
        label: getTrainCustomInputs(t).prognosisType.label,
        typeFormField: `${formPrefix}.prognosisType.type` as const,
        customFormField: `${formPrefix}.prognosisType.description` as const,
      },
    };
  };
  const getItems = () => {
    if (formPrefix === "infrastructureForm")
      return filterOpInfoTypesInfrastructure(opInfoTypes!, false).prognoses;
    return filterOpInfoTypesTrain(opInfoTypes!).prognoses;
  };

  switch (opInfoTypesStatus) {
    case "success":
      return (
        <Stack
          gap="6px"
          marginTop="6px"
          padding={showDateTimeFields ? 3 : 0}
          boxShadow={
            showDateTimeFields
              ? `inset 0 0 0 1px ${theme.colorOutlineMain}`
              : undefined
          }
          borderRadius="sm"
        >
          <FormFieldWithCustomInput<InfrastructureForm | EitherTrainFormSchema>
            items={getItems()}
            {...getCustomInputs().prognosisType}
          />
          {showDateTimeFields && (
            <HStack>
              <TimePicker
                label="Kl"
                value={toTime(prognosisTime)}
                minuteInterval={5}
                onValueChange={(time) => {
                  if (time !== null) {
                    setValue(
                      `${formPrefix}.prognosisTime`,
                      prognosisTime.set({ ...time }),
                      { shouldValidate: true, shouldDirty: true },
                    );
                  }
                }}
              />
              <Field>
                <DropsDatePicker
                  variant="core"
                  label={t("Dato")}
                  value={toCalendarDate(prognosisTime)}
                  onValueChange={(date) => {
                    if (date !== null) {
                      setValue(
                        `${formPrefix}.prognosisTime`,
                        prognosisTime.set({ ...date }),
                        { shouldValidate: true, shouldDirty: true },
                      );
                    }
                  }}
                  withPortal={false}
                />
              </Field>
            </HStack>
          )}
        </Stack>
      );
    case "pending":
      return <Skeleton height="36px" />;
    case "error":
      return (
        <Alert data-color="critical">
          {t(
            "Noe gikk galt ved henting av prognoser. Last siden på nytt for å prøve igjen. Ta kontakt med IT hvis feilen vedvarer.",
          )}
        </Alert>
      );
  }
};
